import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    FormControlLabel,
    Switch,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Alert,
    CircularProgress,
    Chip
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon,
    PlayArrow as StartIcon,
    FlashOn as QuickScanIcon
} from "@mui/icons-material";

const WebsiteScanForm = ({ open, onClose, onStartScan, onQuickScan, loading }) => {
    const [url, setUrl] = useState('');
    const [maxPages, setMaxPages] = useState(100);
    const [maxDepth, setMaxDepth] = useState(3);
    const [delay, setDelay] = useState(1000);
    const [followExternal, setFollowExternal] = useState(false);
    const [respectRobotsTxt, setRespectRobotsTxt] = useState(true);
    const [quickScanResult, setQuickScanResult] = useState(null);
    const [quickScanning, setQuickScanning] = useState(false);

    const handleClose = () => {
        if (!loading && !quickScanning) {
            setUrl('');
            setQuickScanResult(null);
            onClose();
        }
    };

    const handleQuickScan = () => {
        if (!url) {
            alert('Please enter a URL');
            return;
        }

        setQuickScanning(true);
        setQuickScanResult(null);

        onQuickScan(url, (err, data) => {
            setQuickScanning(false);
            if (!err && data) {
                setQuickScanResult(data);
            }
        });
    };

    const handleStartScan = () => {
        if (!url) {
            alert('Please enter a URL');
            return;
        }

        const config = {
            max_pages: maxPages,
            max_depth: maxDepth,
            delay_between_requests: delay,
            follow_external_links: followExternal,
            respect_robots_txt: respectRobotsTxt
        };

        onStartScan(url, config);
    };

    const renderQuickScanResult = () => {
        if (!quickScanResult) return null;

        const { success, product, hasStructuredData, structuredDataCounts } = quickScanResult;

        if (success && product) {
            return (
                <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Product Found: {product.name || 'Unnamed'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        {structuredDataCounts.jsonLd > 0 && (
                            <Chip label={`JSON-LD: ${structuredDataCounts.jsonLd}`} size="small" color="primary" />
                        )}
                        {structuredDataCounts.microdata > 0 && (
                            <Chip label={`Microdata: ${structuredDataCounts.microdata}`} size="small" color="primary" />
                        )}
                        {structuredDataCounts.rdfa > 0 && (
                            <Chip label={`RDFa: ${structuredDataCounts.rdfa}`} size="small" color="primary" />
                        )}
                    </Box>
                    {product.brand && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            Brand: {product.brand}
                        </Typography>
                    )}
                    {product.price && (
                        <Typography variant="body2">
                            Price: {product.price.currency} {product.price.value}
                        </Typography>
                    )}
                </Alert>
            );
        } else if (!hasStructuredData) {
            return (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    No structured data found on this page. This site may not be suitable for scraping.
                </Alert>
            );
        } else {
            return (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Structured data found but couldn't extract complete product information.
                </Alert>
            );
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Start Website Scan</Typography>
                    <Button
                        size="small"
                        startIcon={quickScanning ? <CircularProgress size={16} /> : <QuickScanIcon />}
                        onClick={handleQuickScan}
                        disabled={!url || quickScanning || loading}
                        variant="outlined"
                    >
                        Quick Test
                    </Button>
                </Box>
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField
                        label="Website URL"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://www.example-fashion-store.com"
                        fullWidth
                        required
                        helperText="Enter the homepage or starting URL of the ecommerce website"
                    />

                    {renderQuickScanResult()}

                    <Accordion defaultExpanded={false}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Advanced Configuration</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    label="Max Pages"
                                    type="number"
                                    value={maxPages}
                                    onChange={(e) => setMaxPages(parseInt(e.target.value) || 100)}
                                    fullWidth
                                    helperText="Maximum number of pages to crawl (recommended: 50-200)"
                                />

                                <TextField
                                    label="Max Depth"
                                    type="number"
                                    value={maxDepth}
                                    onChange={(e) => setMaxDepth(parseInt(e.target.value) || 3)}
                                    fullWidth
                                    helperText="Maximum link depth from entry URL (recommended: 2-4)"
                                />

                                <TextField
                                    label="Delay Between Requests (ms)"
                                    type="number"
                                    value={delay}
                                    onChange={(e) => setDelay(parseInt(e.target.value) || 1000)}
                                    fullWidth
                                    helperText="Milliseconds to wait between requests (recommended: 1000-3000)"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={respectRobotsTxt}
                                            onChange={(e) => setRespectRobotsTxt(e.target.checked)}
                                        />
                                    }
                                    label="Respect robots.txt (recommended)"
                                />

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={followExternal}
                                            onChange={(e) => setFollowExternal(e.target.checked)}
                                        />
                                    }
                                    label="Follow external links (not recommended)"
                                />
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleStartScan}
                    disabled={!url || loading || (quickScanResult && !quickScanResult.success)}
                    startIcon={loading ? <CircularProgress size={20} /> : <StartIcon />}
                >
                    {loading ? 'Starting...' : 'Start Full Scan'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WebsiteScanForm;
